import { useState, useEffect } from 'react';

export const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if app is already installed
    const checkIfInstalled = () => {
      // Check if running in standalone mode (PWA is installed)
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      // Check if running in WebView (iOS PWA)
      const isWebView = (window.navigator as any).standalone;
      // Check if user agent contains 'wv' (Android WebView)
      const isAndroidWebView = /wv/.test(window.navigator.userAgent);
      
      setIsInstalled(isStandalone || isWebView || isAndroidWebView);
    };

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Save the event so it can be triggered later
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsInstallable(true);
    };

    // Listen for the appinstalled event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    checkIfInstalled();

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const installPWA = async () => {
    if (!deferredPrompt) {
      // If no beforeinstallprompt event is available, we can't programmatically install
      // The user will need to use browser's built-in install option
      return false;
    }

    try {
      // Show the install prompt
      await deferredPrompt.prompt();
      
      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setIsInstalled(true);
        setIsInstallable(false);
        setDeferredPrompt(null);
        return true;
      }
      
      return false;
    } catch (error) {
      return false;
    }
  };

  const shouldShowInstallPrompt = () => {
    
    // Don't show if already installed
    if (isInstalled) {
      return false;
    }
    
    // Don't show if already shown in this session
    const shownThisSession = sessionStorage.getItem('pwa-install-shown');
    if (shownThisSession) {
      return false;
    }
    
    // Check if user has dismissed the prompt recently (within 7 days)
    const lastDismissed = localStorage.getItem('pwa-install-dismissed');
    if (lastDismissed) {
      const dismissedDate = new Date(lastDismissed);
      const now = new Date();
      const daysSinceDismissed = (now.getTime() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        return false;
      }
    }
    
    // Check if this is a mobile device
    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isInBrowser = !window.matchMedia('(display-mode: standalone)').matches;
    
    // Check if running in a PWA-capable browser
    const isPWACapable = 'serviceWorker' in navigator;
  
    // Show the prompt if:
    // 1. We have the beforeinstallprompt event (isInstallable) - preferred method
    // 2. OR we're on mobile, in browser, and PWA capable (fallback for when event doesn't fire)
    const shouldShow = isInstallable || (isMobile && isInBrowser && isPWACapable);
    
    return shouldShow;
  };

  const dismissInstallPrompt = () => {
    localStorage.setItem('pwa-install-dismissed', new Date().toISOString());
    sessionStorage.setItem('pwa-install-shown', 'true');
  };

  return {
    isInstallable,
    isInstalled,
    installPWA,
    shouldShowInstallPrompt,
    dismissInstallPrompt
  };
};
